import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Link as ChakraLink,
  SimpleGrid,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../compnents/Navbar';
import { useTranslation } from '../i18n';
import Footer from '../compnents/Footer';
import axios from 'axios';
import { buildPurchaseIntentUrl, normalizePurchaseIntent } from '../utils/purchaseIntent';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction ? process.env.REACT_APP_API_BASE_URL_PROD : process.env.REACT_APP_API_BASE_URL_DEV;

const darkInputProps = {
  bg: 'rgba(255, 255, 255, 0.05)',
  color: 'white',
  borderColor: 'brand.quantumBorder',
  _placeholder: { color: 'whiteAlpha.500' },
  _hover: { borderColor: 'brand.quantumAccent' },
  _focus: {
    borderColor: 'brand.quantumAccent',
    boxShadow: '0 0 0 1px var(--chakra-colors-brand-quantumAccent)',
  },
};

const VerifyOtpPage = () => {
  const [otp, setOtp] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();
  const purchaseIntent = useMemo(
    () => normalizePurchaseIntent(location.search),
    [location.search]
  );
  const loginHref = buildPurchaseIntentUrl('/login', purchaseIntent);
  const registerHref = buildPurchaseIntentUrl('/register', purchaseIntent);

  useEffect(() => {
    const queryEmail = new URLSearchParams(location.search).get('email');
    const storedEmail = sessionStorage.getItem('pendingVerificationEmail');
    const nextEmail = location.state?.email || queryEmail || storedEmail || '';

    if (nextEmail) {
      setEmail(nextEmail);
    }
  }, [location.search, location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      toast({
        title: t('otp.toast.emailRequired.title'),
        description: t('otp.toast.emailRequired.body'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/verify`, { email, otp });
      const data = response.data;

      toast({
        title: t('otp.toast.verified.title'),
        description: data.message || t('otp.toast.verified.body'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      sessionStorage.removeItem('pendingVerificationEmail');
      navigate(loginHref, {
        state: {
          email,
          verified: true,
        },
      });
    } catch (error) {
      const errorMessage = error.response?.data?.message || t('otp.toast.failed.body');
      toast({
        title: t('otp.toast.failed.title'),
        description: errorMessage,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!email) {
      toast({
        title: t('otp.toast.emailRequired.title'),
        description: t('otp.toast.emailRequired.body'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setResendLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/resend-otp`, { email });
      const data = response.data;

      toast({
        title: t('otp.toast.resent.title'),
        description: data.message || t('otp.toast.resent.body'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      const errorMessage = error.response?.data?.message || t('otp.toast.resendFailed.body');
      toast({
        title: t('otp.toast.resendFailed.title'),
        description: errorMessage,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Box minH="100vh" display="flex" flexDirection="column" bg="brand.quantumBg">
      <Navbar />

      <Box className="grid-bg-dark quantum-mesh" flex="1" overflow="hidden">
        <Container maxW="container.xl" position="relative" zIndex={1} py={{ base: 10, md: 16 }}>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 8, md: 10 }} alignItems="start">
            <VStack align="stretch" spacing={6}>
              <Badge
                alignSelf="flex-start"
                bg="rgba(0, 212, 170, 0.1)"
                color="brand.quantumAccent"
                borderRadius="full"
                px={3}
                py={1}
                letterSpacing="0.15em"
                textTransform="uppercase"
              >
                {t('otp.badge')}
              </Badge>
              <Box>
                <Heading as="h1" fontSize={{ base: '38px', md: '56px' }} color="white" lineHeight="1.05">
                  {t('otp.hero.title')}
                </Heading>
                <Text mt={4} color="brand.quantumMuted" fontSize="lg" maxW="2xl">
                  {t('otp.hero.body')}
                </Text>
              </Box>

              <Box
                bg="rgba(0, 212, 170, 0.08)"
                borderRadius="2xl"
                borderWidth="1px"
                borderColor="rgba(0, 212, 170, 0.3)"
                p={{ base: 5, md: 6 }}
              >
                <VStack align="stretch" spacing={4}>
                  <Text fontSize="sm" fontWeight="800" color="brand.quantumAccent" textTransform="uppercase" letterSpacing="0.15em">
                    {t('otp.next.title')}
                  </Text>
                  <Text color="brand.quantumInk" fontSize="sm">
                    {t('otp.next.one')}
                  </Text>
                  <Text color="brand.quantumInk" fontSize="sm">
                    {t('otp.next.two')}
                  </Text>
                  <Text color="brand.quantumInk" fontSize="sm">
                    {t('otp.next.three')}
                  </Text>
                </VStack>
              </Box>
            </VStack>

            <Box
              bg="brand.quantumCard"
              p={{ base: 6, md: 8 }}
              borderRadius="2xl"
              boxShadow="0 24px 80px rgba(0, 0, 0, 0.38)"
              borderWidth="1px"
              borderColor="brand.quantumBorder"
            >
              <VStack spacing={6} align="center">
                <Box textAlign="center">
                  <Heading as="h2" size="lg" mb={3} color="white" fontWeight="bold">
                    {t('otp.card.title')}
                  </Heading>
                  <Text color="brand.quantumMuted" fontSize="md">
                    {t('otp.card.body')}
                  </Text>
                </Box>

                <Alert status={resendLoading ? 'info' : 'success'} borderRadius="lg" alignItems="flex-start">
                  <AlertIcon mt={1} />
                  <AlertDescription fontSize="sm">
                    {resendLoading
                      ? t('otp.alert.sending')
                      : t('otp.alert.waiting')}
                  </AlertDescription>
                </Alert>

                <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                  <VStack spacing={6}>
                    <FormControl isRequired>
                      <FormLabel fontWeight="700" color="white">{t('otp.field.email')}</FormLabel>
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        size="lg"
                        {...darkInputProps}
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel fontWeight="700" color="white">{t('otp.field.code')}</FormLabel>
                      <Input
                        type="text"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        maxLength={6}
                        textAlign="center"
                        fontSize="2xl"
                        letterSpacing="wider"
                        size="lg"
                        height="60px"
                        {...darkInputProps}
                      />
                      <Text fontSize="sm" color="brand.quantumMuted" mt={2} textAlign="center">
                        {t('otp.field.codeHelp')}
                      </Text>
                    </FormControl>

                    <VStack spacing={4} width="full">
                      <Button
                        type="submit"
                        size="lg"
                        width="full"
                        isLoading={loading}
                        loadingText={t('otp.cta.verifying')}
                        bgGradient="linear(135deg, brand.quantumAccent, brand.quantumCyan)"
                        color="brand.quantumBg"
                        _hover={{ opacity: 0.9 }}
                        fontSize="md"
                        fontWeight="800"
                        py={6}
                      >
                        {t('otp.cta.verify')}
                      </Button>

                      <HStack spacing={4} width="full">
                        <Button
                          type="button"
                          variant="outline"
                          size="lg"
                          flex={1}
                          isLoading={resendLoading}
                          loadingText={t('otp.cta.resending')}
                          onClick={handleResendOtp}
                          borderColor="whiteAlpha.300"
                          color="white"
                          _hover={{ bg: 'whiteAlpha.100' }}
                        >
                          {t('otp.cta.resend')}
                        </Button>

                        <Button
                          type="button"
                          variant="ghost"
                          size="lg"
                          flex={1}
                          color="brand.quantumAccent"
                          _hover={{ bg: 'whiteAlpha.100', color: 'brand.quantumCyan' }}
                          onClick={() => navigate(registerHref)}
                        >
                          {t('otp.cta.back')}
                        </Button>
                      </HStack>
                    </VStack>
                  </VStack>
                </form>

                <Text fontSize="sm" color="brand.quantumMuted" textAlign="center">
                  {t('otp.wrongEmail')}{' '}
                  <ChakraLink as={RouterLink} to={registerHref} color="brand.quantumAccent" fontWeight="800">
                    {t('otp.restart')}
                  </ChakraLink>
                </Text>
              </VStack>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default VerifyOtpPage;
